import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";
import PasswordForm from "@components/Forms/PasswordForm";
import { XCircleIcon } from "@heroicons/react/16/solid";

import { GetPasswordPolicy } from "@services/Utilities";
import { error_message, UserFieldErrorMessages } from "@constants/Errors";

interface PasswordResetModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onPasswordResetSuccess: () => void;
  isFromUserPasswordScreen?: boolean;
  confirmButtonText?: string;
  isReset?: boolean;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onRequestClose,
  onPasswordResetSuccess,
  confirmButtonText = "変更する",
  isFromUserPasswordScreen = true,
  isReset = true,
}) => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const noMatchToast = () => toast.error(error_message.password.no_match);
  const blankFieldToast = () => toast.error(error_message.password.blank_field);
  const nonPolicyToast = () => toast.error(error_message.password.bad_policy);

  const passwordKey: string = "password";
  const verifyPasswordKey: string = "verifyPassword";
  const passwordPolicy: string = GetPasswordPolicy();

  const [errors, setErrors] = useState<{
    password: boolean;
    verifyPassword: boolean;
    noMatch: boolean;
  }>({
    password: false,
    verifyPassword: false,
    noMatch: false,
  });

  const verifyFields = () => {
    return !Object.values(errors).some((error) => error === true);
  };

  const updateError = (field: keyof typeof errors, value: boolean) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: value,
    }));
  };

  const verifyBlankFields = () => {
    return newPassword !== "" && confirmPassword !== "";
  };

  const verifyNewPassword = () => {
    return newPassword === confirmPassword;
  };

  const verifyPasswords = (): boolean => {
    if (!verifyBlankFields()) {
      blankFieldToast();
      return false;
    }
    if (!verifyNewPassword()) {
      noMatchToast();
      return false;
    }

    if (!verifyFields()) {
      nonPolicyToast();
      return false;
    }

    return true;
  };

  const resetFields = () => {
    setConfirmPassword("");
    setNewPassword("");
    setErrors({
      password: false,
      verifyPassword: false,
      noMatch: false,
    });
  };

  const onCancel = () => {
    resetFields();
    onRequestClose();
  };

  const onConfirm = async () => {
    if (verifyPasswords()) {
      onPasswordResetSuccess(confirmPassword);
      onRequestClose();
      setNewPassword("");
      setConfirmPassword("");
    } else {
      return;
    }
  };

  useEffect(() => {
    setNewPassword("");
    setConfirmPassword("");
    setErrors({
      password: false,
      verifyPassword: false,
      noMatch: false,
    });
  }, [isOpen]);

  useEffect(() => {
    if (newPassword != confirmPassword) {
      updateError("noMatch", true);
    } else {
      updateError("noMatch", false);
    }

    if (newPassword === "" || confirmPassword === "") {
      updateError("noMatch", false);
    }
  }, [newPassword, confirmPassword]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center backdrop-brightness-50 z-[1000]">
      <div className="bg-white border border-text-field-border rounded-lg sm:w-3/4 md:w-1/3 lg:w-1/2">
        <div className="mayo-card-header p-4 flex justify-between items-center">
          <div className="">
            {isReset ? "パスワード変更" : "パスワード設定"}
          </div>
          <button onClick={onRequestClose} className="text-xl hover:opacity-50">
            <svg
              className="ml-2 fill-body hover:fill-primary"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <XCircleIcon></XCircleIcon>
            </svg>
          </button>
        </div>

        <div className="ml-5">
          <div className="mb-5 mt-5">
            <PasswordForm
              label={
                isFromUserPasswordScreen ? "変更後のパスワード" : "パスワード"
              }
              password={newPassword}
              setPassword={setNewPassword}
              modal={true}
              identifier={passwordKey}
              errors={errors.password}
              setError={updateError}
            />
          </div>

          <div>
            <PasswordForm
              label={
                isFromUserPasswordScreen
                  ? "変更後のパスワード（再入力）"
                  : "パスワード（再入力）"
              }
              password={confirmPassword}
              setPassword={setConfirmPassword}
              modal={true}
              identifier={verifyPasswordKey}
              errors={errors.verifyPassword}
              setError={updateError}
            />
          </div>
        </div>

        <div className="ml-5 mt-5">
          {errors.noMatch && (
            <p className="text-red-500">
              {UserFieldErrorMessages.passwordNoMatchError}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <div className="ml-5 pt-10 text-zinc-400 font-bold">
            {"パスワードポリシー: "}
          </div>
          <div className="ml-5 pt-10 text-zinc-400 font-bold">{`${passwordPolicy}`}</div>
        </div>

        <div className="mb-5">
          <ConfirmCancelButtons
            confirmText={confirmButtonText}
            onConfirm={onConfirm}
            onCancel={onCancel}
            form={false}
          />
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;
